import type { Annotation } from "building-types/annotation/annotationTypes";
import type { BlockPerimeters } from "building-types/blocks/blockTypes";
import type { Building } from "building-types/building/buildingTypes";
import type { ReferenceGeom } from "building-types/entities/referenceGeom";
import type { Room } from "building-types/rooms/roomTypes";
import type { RWTable, Table } from "common/data/table";
import { arrayShift } from "common/utils/arrayShift";
import objectMap from "common/utils/objectMap";
import { buildingIsMirrored } from "doc/options/buildingIsMirrored";
import { roomAnnotationTableFlipOverYAxis } from "doc/utils/annotation";
import { mirrorTranslationXFromPolyLine2s } from "geom/aabb2";
import {
  polyLine2Copy,
  polyLine2IsClosed,
  polyLine2Move,
  polyLine2ReflectOverYAxis,
  polyLine2Reverse,
  polyLine2Shift,
} from "geom/polyLine2";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

function select({
  rooms,
  blockPerimeters,
  roomAnnotation,
  doc: { optionSets, referenceGeom, assets },
  optionChoices,
  mirrorTranslationXOverride,
  mirrorInvert,
}: Building) {
  return {
    rooms,
    blockPerimeters,
    mirror: buildingIsMirrored(optionSets, optionChoices) !== !!mirrorInvert,
    roomAnnotation,
    referenceGeom,
    assets,
    mirrorTranslationXOverride,
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  referenceGeom: "shallow",
  rooms: "shallow",
  blockPerimeters: "shallow",
  mirror: "shallow",
  roomAnnotation: "shallow",
  assets: "shallow",
  mirrorTranslationXOverride: "shallow",
});

function generate({
  rooms,
  blockPerimeters,
  roomAnnotation,
  mirror,
  referenceGeom,
  assets,
  mirrorTranslationXOverride,
}: ReturnType<typeof select>): Partial<{
  mirror: boolean;
  mirrorTranslationX: number;
  rooms: Table<Room>;
  blockPerimeters: BlockPerimeters;
  roomAnnotation: Table<Annotation[]>;
  referenceGeom: Table<ReferenceGeom>;
}> {
  // Passthrough doc reference geom so downstream stages can read it from the building
  if (!mirror) {
    return {
      mirror: false,
      mirrorTranslationX: 0,
      referenceGeom,
    };
  }

  // The override can be used to mirror over a specific axis
  // Otherwise preserve horizontal center of mass from generated room outlines.
  const mirrorTranslationX =
    mirrorTranslationXOverride ?? mirrorTranslationXFromPolyLine2s(Object.values(rooms).map((x) => x.outline));

  const reflectedRooms: RWTable<Room> = {};
  for (const roomId in rooms) {
    const room = rooms[roomId];

    const outline = polyLine2ReflectOverYAxis(polyLine2Copy(room.outline));
    polyLine2Shift(polyLine2Reverse(outline), 1);
    polyLine2Move(outline, mirrorTranslationX, 0);

    const edgeIds = arrayShift(room.edgeIds.slice().reverse(), 1);

    reflectedRooms[roomId] = {
      ...room,
      outline,
      edgeIds,
    };
  }

  const reflectedReferenceGeom = objectMap(referenceGeom, (g) => {
    if (g.type === "POLYLINE2") {
      const geom = polyLine2ReflectOverYAxis(polyLine2Copy(g.geom));
      polyLine2IsClosed(geom) ? polyLine2Shift(polyLine2Reverse(geom), 1) : polyLine2Reverse(geom);
      polyLine2Move(geom, mirrorTranslationX, 0);

      const edgeIds = arrayShift(g.edgeIds.slice().reverse(), 1);

      return { ...g, geom, edgeIds };
    }

    return g;
  });

  const reflectedBlockPerimeters = {};
  for (const blockId in blockPerimeters) {
    const perimeters = blockPerimeters[blockId];

    reflectedBlockPerimeters[blockId] = perimeters.map((perimeter) => {
      const ref = polyLine2ReflectOverYAxis(polyLine2Copy(perimeter));
      polyLine2Move(ref, mirrorTranslationX, 0);
      return polyLine2Shift(polyLine2Reverse(ref), 1);
    });
  }

  return {
    mirror,
    mirrorTranslationX,
    referenceGeom: reflectedReferenceGeom,
    rooms: reflectedRooms,
    blockPerimeters: reflectedBlockPerimeters,
    roomAnnotation: roomAnnotationTableFlipOverYAxis(roomAnnotation, assets, mirrorTranslationX),
  };
}

export default memo(select, generate, compare);
